import { IRecordOf } from '@opengov/compose-app/immutable';
import { BasicColumnTypes } from '@opengov/ogql-types';
import { EReportChartType, IReportConfiguration, OrganizedByColumnType, CalendarUnit } from '@opengov/reporting-types';
import {
  EOgqlAggregateColumnOperators,
  EReportColumnTypes,
  getDisplayName,
  IReportColumn
} from '@opengov/reporting-types/src/reportColumns-immer';

import { startCase, truncate } from 'lodash';

import {
  AmountType,
  amountTypes,
  calculateColumnAmountType,
  calendarUnitFormatters
} from '@opengov/reporting-formatters/formatters';

import { ensureString } from './ogql';

const defaultLabelMinWidth = 50;
const maxYAxisTickLabelLength = 26;
const maxXTickLength = 18;
const maxYAxisLabelLength = 19;

const amountTypeAttributeForColumn = (column, key, alternative) => {
  if (column) {
    const amountType = amountTypes[calculateColumnAmountType(column)];
    if (amountType) {
      return amountType[key];
    }
  } else if (column.name === 'count') {
    return amountTypes[AmountType.Numeric][key];
  }
  return alternative;
};

// pick a "sign" for the number- dollars, percents xor NA
export const numericFormatterOfColumnsAndName = (column: IReportColumn): ((domain: string | number) => string) => {
  return column ? amountTypeAttributeForColumn(column, `formatter`, (domain) => `${domain}`) : (domain) => `${domain}`;
};

interface IAxisConfiguration {
  label: string;
  tickLabelWidth: number;
  tickLabelFormatter(domain: string | number): string;
}

const getChartAxisConfiguration = (
  chartType: EReportChartType,
  {
    domain,
    range
  }: {
    domain: IAxisConfiguration;
    range: IAxisConfiguration;
  }
): {
  x: IAxisConfiguration;
  y: IAxisConfiguration;
} => {
  if (chartType === EReportChartType.HorizontalBar) {
    return {
      x: range,
      y: domain
    };
  } else {
    return {
      x: domain,
      y: range
    };
  }
};

export type ICreateTickLabelFormattersParams = {
  countString: string;
  emptyValues: string;

  columnNames: IReportConfiguration['presentationConfiguration']['columnNames'];
  showColumn: IReportConfiguration['visualizationQuery']['showColumn'];
  organizedBy: IReportConfiguration['visualizationQuery']['organizedBy'];

  showColumnAsPercentOfTotal: boolean;
  entityFiscalYearStartMonth: number;
  visualizationChartType: EReportChartType;
  organizedByColumn: IReportColumn;
};

export const createTickLabelFormatters = (params: ICreateTickLabelFormattersParams) => {
  const {
    columnNames,
    countString,
    emptyValues,
    visualizationChartType,
    entityFiscalYearStartMonth,
    showColumnAsPercentOfTotal,
    organizedBy,
    organizedByColumn,
    showColumn
  } = params;

  const tickLabelFormatter = (domain: string | number) =>
    `${
      organizedByColumn && organizedByColumn.type === BasicColumnTypes.Boolean
        ? startCase(ensureString(domain as string))
        : domain || emptyValues
    }`;

  const axisDomainConfig = {
    tickLabelFormatter,
    tickLabelWidth: defaultLabelMinWidth,
    label: organizedByColumn && organizedByColumn.displayName
  };

  const axisRangeConfig = {
    tickLabelFormatter: showColumnAsPercentOfTotal
      ? (y: any) => amountTypes[AmountType.Percent].formatter(y)
      : numericFormatterOfColumnsAndName(showColumn),
    tickLabelWidth: defaultLabelMinWidth,
    label:
      showColumn.columnType === EReportColumnTypes.aggregate &&
      showColumn.aggregateType === EOgqlAggregateColumnOperators.count
        ? countString
        : getDisplayName(showColumn as IRecordOf<IReportColumn>, columnNames)
  };

  axisDomainConfig.label = organizedByColumn && organizedByColumn.displayName;

  if (
    organizedBy.selection &&
    (organizedBy.columnType === OrganizedByColumnType.Date || organizedBy.selection === CalendarUnit.FiscalPeriod)
  ) {
    const { formatter } = calendarUnitFormatters[organizedBy.selection];
    let fiscalYearStartMonth = entityFiscalYearStartMonth;

    // FiscalQuarter is already translated
    if (organizedBy.selection === CalendarUnit.FiscalQuarter) {
      fiscalYearStartMonth = 1;
    }

    axisDomainConfig.tickLabelFormatter = formatter({ entityFiscalYearStartMonth: fiscalYearStartMonth });
  }

  const axisConfig = getChartAxisConfiguration(visualizationChartType, {
    domain: axisDomainConfig,
    range: axisRangeConfig
  });
  const yTickLabelFormatter = axisConfig.y.tickLabelFormatter || ((x) => `${x}`);

  return {
    xLabel: axisConfig.x.label,
    yLabel:
      visualizationChartType === EReportChartType.HorizontalBar
        ? truncate(axisConfig.y.label, { length: maxYAxisLabelLength })
        : truncate(axisConfig.y.label, { length: 100 }),
    xTickLabelFormatter: (x) => truncate(axisConfig.x.tickLabelFormatter(x), { length: maxXTickLength }),
    xLabelFormatter: axisConfig.x.tickLabelFormatter,
    xTickLabelWidth: axisConfig.x.tickLabelWidth,
    yTickLabelFormatter: (x) => truncate(yTickLabelFormatter(x), { length: maxYAxisTickLabelLength }),
    yTickLabelWidth: axisConfig.y.tickLabelWidth
  };
};
